import { RemoveMemberSuccessDescription } from "@components/remove-member-success-description";
import { DeleteIcon, Dialog, MenuItem, Text, useToast } from "@flat-ui";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/dialog-constants";
import { UserDisplay } from "@components/common/user-display";
import { useRefreshSeatUsage } from "@components/table/members/assign-seats/use-refresh-seat-usage";
import { fetchingMembersFlagsSelector } from "@store/members/members-selector";
import { removeMemberFromCompany } from "@store/members/members-slice-thunk";
import { selectedSdbCompanyNameSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { fetchingTeamsFlagsSelector } from "@store/teams/teams-selector";
import { removeMembersFromTeam } from "@store/teams/teams-slice-thunk";
import { TableType, TeamMemberEvents, WorkspaceMemberEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";

/** Determines from where a member should be removed */

const REMOVE_TARGET_DISPLAY_NAMES = {
  team: TEAM_DISPLAY_NAME,
  workspace: "workspace"
};
/**
 * Menu button to remove a member from the team, from the workspace, or from either of the two.
 * The removal targets the user is allowed to use are offered in a sub-menu, unless there is only
 * one of them. Selecting a target opens a confirmation dialog.
 */
export function TeamRemoveMenuButton({
  companyId,
  team,
  member,
  canRemoveFromTeam,
  canRemoveFromWorkspace,
  onMenuClose
}) {
  const dispatch = useAppDispatch();
  const {
    openToast
  } = useToast();
  const {
    trackEvent
  } = useTrackEvent();
  const companyName = useAppSelector(selectedSdbCompanyNameSelector);
  const refreshSeatUsage = useRefreshSeatUsage();
  const [removeTarget, setRemoveTarget] = useState(null);
  const {
    isRemovingTeamMember
  } = useAppSelector(fetchingTeamsFlagsSelector);
  const {
    isRemovingCompanyMember
  } = useAppSelector(fetchingMembersFlagsSelector);
  const isRemoving = removeTarget === "team" ? isRemovingTeamMember : isRemovingCompanyMember;
  function closeDialog() {
    setRemoveTarget(null);
    onMenuClose();
  }
  async function confirmRemove() {
    try {
      switch (removeTarget) {
        case "team":
          await removeFromTeam();
          break;
        case "workspace":
          await removeFromWorkspace();
          break;
      }
    } catch {
      // Errors are handled by the respective slices
    }
  }
  async function removeFromTeam() {
    trackEvent({
      name: TeamMemberEvents.removeMemberFromTeam,
      props: {
        dataType: TableType.teamMembers
      }
    });
    try {
      await dispatch(removeMembersFromTeam({
        companyId,
        teamId: team.id,
        memberIds: [member.identity]
      })).unwrap();
      openToast({
        title: `Member removed from ${TEAM_DISPLAY_NAME}`,
        message: <RemoveMemberSuccessDescription member={member} subjectType="team" entityName={team.name} />
      });
    } finally {
      closeDialog();
    }
  }
  async function removeFromWorkspace() {
    trackEvent({
      name: WorkspaceMemberEvents.removeMember,
      props: {
        dataType: TableType.teamMembers
      }
    });
    try {
      await dispatch(removeMemberFromCompany({
        companyId,
        member
      })).unwrap();
      refreshSeatUsage();
      openToast({
        title: "Member removed from workspace",
        message: <RemoveMemberSuccessDescription member={member} subjectType="workspace" entityName={companyName ?? ""} />
      });
    } finally {
      closeDialog();
    }
  }
  const removeTargets = [...(canRemoveFromTeam ? ["team"] : []), ...(canRemoveFromWorkspace ? ["workspace"] : [])];
  const removeTargetDisplayName = removeTarget ? REMOVE_TARGET_DISPLAY_NAMES[removeTarget] : "";
  return <>
      {removeTargets.length === 1 ?
    // Named after its target, so that it is not mistaken for removing the member from the
    // team, which is what an unqualified label means on a list of team members.
    <MenuItem label={`Remove from ${REMOVE_TARGET_DISPLAY_NAMES[removeTargets[0]]}`} Icon={DeleteIcon} onClick={() => setRemoveTarget(removeTargets[0])} /> : <MenuItem label="Remove member" Icon={DeleteIcon} subMenuTrigger="click">
          {removeTargets.map(target => <MenuItem key={target} label={`From ${REMOVE_TARGET_DISPLAY_NAMES[target]}`} onClick={() => setRemoveTarget(target)} />)}
        </MenuItem>}

      <Dialog title={`Remove from ${removeTargetDisplayName}`} confirmText="Remove" open={removeTarget !== null} onConfirm={confirmRemove} onCancel={closeDialog} showSpinner={isRemoving}>
        <Text variant="bodyM" sx={{
        marginBottom: SPACE_ELEMENTS_OF_MODAL
      }}>
          When removing member from {removeTargetDisplayName} they will no
          longer be able to access this {removeTargetDisplayName}. You can
          always invite this member again.
        </Text>

        <UserDisplay member={member} />
      </Dialog>
    </>;
}